<!--
  - Copyright (C) 2024. Archimedes Exhibitions GmbH,
  - Saarbrücker Str. 24, Berlin, Germany
  -
  - This file contains proprietary source code and confidential
  - information. Its contents may not be disclosed or distributed to
  - third parties unless prior specific permission by Archimedes
  - Exhibitions GmbH, Berlin, Germany is obtained in writing. This applies
  - to copies made in any form and using any medium. It applies to
  - partial as well as complete copies.
  -->

<template>
  <div>
    <b-form>
      <b-form-group
        :label="$t('users.common.user') + ':'"
        description="Username can not be changed"
      >
        <b-form-input
          required
          :disabled="!!user.id"
          :state="!user.username ? false : null"
          v-model="user.username"
        >
        </b-form-input>
      </b-form-group>
      <b-form-group :label="$t('users.common.firstName') + ':'">
        <b-form-input
          v-model="user.first_name"
          :state="!user.first_name ? false : null"
          required
        ></b-form-input>
      </b-form-group>
      <b-form-group :label="$t('users.common.lastName') + ':'">
        <b-form-input
          v-model="user.last_name"
          :state="!user.last_name ? false : null"
          required
        ></b-form-input>
      </b-form-group>
      <b-form-group :label="$t('users.common.email') + ':'">
        <b-form-input
          v-model="user.email"
          :state="!user.email || !validateEmail(user.email) ? false : null"
          required
        ></b-form-input>
      </b-form-group>
      <b-row>
        <b-col>
          <b-form-group :label="$t('users.common.enabled') + ':'">
            <b-form-checkbox v-model="user.is_enabled"></b-form-checkbox>
          </b-form-group>
        </b-col>
        <b-col style="border-left: solid 1px rgba(0, 0, 0, 0.125)" >
          <b-form-group :label="$t('users.common.roles') + ':'">
            <b-form-checkbox-group
              v-model="user.roles"
              value-field="id"
              :options="roles"
              text-field="name"
            ></b-form-checkbox-group>
          </b-form-group>
        </b-col>
      </b-row>
      <hr />
    </b-form>
    <b-button
      :disabled="!user.username || isLoading"
      variant="success"
      block
      @click="onSave"
    >
      {{ $t('users.common.save') }}
      <b-spinner small v-if="isLoading"></b-spinner>
    </b-button>
  </div>
</template>

<script>
  import { utils } from '@amdx/emslib'

  export default {
    name: 'TourEdit',
    components: {},
    props: {
      initUser: {
        default: null
      },
      roles: {
        default: null
      }
    },
    data () {
      return {
        isLoading: false,
        password: '',
        user: {}
      }
    },
    watch: {
      initUser: {
        immediate: true,
        handler () {
          if (!this.initUser) {
            return
          }
          this.prepareUser(this.initUser)
        }
      }
    },
    methods: {
      prepareUser (data) {
        this.user = JSON.parse(JSON.stringify(data))
        let roles = []
        if (this.user.roles) {
          for (let g of this.user.roles) {
            roles.push(g.id)
          }
        }
        this.user.roles = roles
        this.isLoading = false
      },

      validateEmail (address) {
        return (
          address && /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(address)
        )
      },

      async handleRoles (userData) {
        let rolesToAdd = []
        let rolesToRemove = []
        for (let role of this.roles) {
          if (
            userData.roles.find((g) => g.id === role.id) &&
            !this.initUser.roles.find((g) => g.id === role.id)
          ) {
            rolesToAdd.push(role)
          } else if (
            !userData.roles.find((g) => g.id === role.id) &&
            this.initUser.roles.find((g) => g.id === role.id)
          ) {
            rolesToRemove.push(role)
          }
        }

        if (rolesToAdd.length) {
          await this.$userManager.addRolesToUser(this.user.id, rolesToAdd)
        }
        if (rolesToRemove.length) {
          await this.$userManager.removeRolesFromUser(
            this.user.id,
            rolesToRemove
          )
        }
      },

      onSave () {
        let userData = JSON.parse(JSON.stringify(this.user))
        userData.is_enabled = !!userData.is_enabled

        let userRoles = []
        for (let id of userData.roles) {
          let role = this.roles.find((g) => g.id === id)
          userRoles.push(role)
        }
        userData.roles = userRoles

        this.isLoading = true
        if (this.user.id) {
          delete userData.session

          this.$userManager
            .updateUser(userData, this.user.id)
            .then(async () => {
              await this.handleRoles(userData)
              this.$emit('updated', userData.username)
              utils.toast(this, 'Keycloak response ', 'OK! User data saved')
            })
            .catch((error) => {
              this.isLoading = false
              utils.toast(this, 'Keycloak response', error, 'danger')
            })
        } else {
          this.$userManager
            .createUser(userData)
            .then(async (data) => {
              this.user = data
              await this.handleRoles(userData)
              this.$emit('updated', userData.username)
              utils.toast(this, 'Keycloak response ', 'OK! User created')
            })
            .catch((error) => {
              this.isLoading = false
              utils.toast(this, 'Keycloak response', error, 'danger')
            })
        }
      }
    }
  }
</script>

<style lang="scss" scoped></style>
